import { useState, FormEvent } from 'react';
import { useNavigate, useLocation, Link as RouterLink, Navigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Alert,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  HealthAndSafety,
  Badge,
  Lock,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
} from '@mui/icons-material';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../context/AuthContext';
import { cuitDigits, cuitToLoginEmail } from '../../utils/format';
import { validateCuit } from '../../utils/validation';
import { brand } from '../../config/appConfig';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading: authLoading } = useAuth();

  const [cuit, setCuit] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Si ya esta logueado, redirigir al dashboard
  if (!authLoading && session) {
    const from = (location.state as { from?: string })?.from || '/';
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validar el CUIT antes de intentar el login (mensaje claro y sin pegarle
    // al servidor con un CUIT mal formado).
    const digits = cuitDigits(cuit);
    if (digits.length !== 11 || !validateCuit(digits)) {
      setError('Ingresa un CUIT valido (11 digitos).');
      return;
    }

    setSubmitting(true);

    // La identidad de login es el email sintetico derivado del CUIT.
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: cuitToLoginEmail(digits),
      password,
    });

    if (signInError) {
      setSubmitting(false);
      // Mensajes genericos por seguridad (no revelar si el CUIT existe)
      const code = signInError.message.toLowerCase();
      if (code.includes('invalid')) {
        setError('Credenciales invalidas. Verifica el CUIT y la contrasena.');
      } else if (code.includes('disabled') || code.includes('banned')) {
        setError('Cuenta deshabilitada. Contacte al administrador.');
      } else if (code.includes('email not confirmed')) {
        setError('Tu cuenta aun no fue confirmada. Revisa tu correo.');
      } else {
        setError('No pudimos iniciar sesion. Intenta nuevamente.');
      }
      return;
    }

    // El AuthContext detecta el cambio de sesion y redirige automaticamente
    navigate('/', { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'linear-gradient(135deg, #0d47a1 0%, #1565C0 50%, #42a5f5 100%)',
        p: 2,
      }}
    >
      <Card sx={{ width: 440, maxWidth: '100%', boxShadow: 8 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
              }}
            >
              <HealthAndSafety sx={{ fontSize: 36 }} />
            </Avatar>
            {brand.clientName && (
              <Typography variant="h5" fontWeight={700} color="primary">
                {brand.clientName}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Sistema de Gestion Sindical
            </Typography>
          </Box>

          <form onSubmit={handleSubmit} noValidate>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              required
              autoFocus
              autoComplete="username"
              label="CUIT"
              type="text"
              inputMode="numeric"
              placeholder="20-12345678-3"
              value={cuit}
              onChange={(e) => setCuit(e.target.value)}
              disabled={submitting}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Badge color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              required
              autoComplete="current-password"
              label="Contrasena"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              sx={{ mb: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((p) => !p)}
                      edge="end"
                      size="small"
                      aria-label={showPassword ? 'Ocultar clave' : 'Mostrar clave'}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: 'right', mb: 3 }}>
              <Link
                component={RouterLink}
                to="/auth/forgot-password"
                variant="body2"
                underline="hover"
              >
                Olvide mi contrasena
              </Link>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || !cuit || !password}
              startIcon={
                submitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <LoginIcon />
                )
              }
              sx={{ py: 1.5, fontSize: '1rem' }}
            >
              {submitting ? 'Ingresando...' : 'Iniciar Sesion'}
            </Button>
          </form>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 3 }}
          >
            v2.0.0 — ATSA Asociacion de Trabajadores de la Sanidad Argentina
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
